import { forwardRef } from "react";
import {
  Sprout, TrendingUp, TrendingDown, Flame, Wallet,
} from "lucide-react";
import { formatMoney } from "@/lib/currency";

/**
 * SnapshotCard — the off-screen 1080×1080 canvas that gets rasterised
 * to PNG for social sharing. Extracted from MonthlySnapshotModal in
 * iter-45 to keep the modal orchestrator under 150 lines.
 *
 * The card is currently render-only English (the exported PNG is
 * intended for sharing to social feeds — a mixed-language image is
 * confusing); if we later ship localised export, we'd read from the
 * `t()` inside the parent and pass strings in as props.
 */
const SnapshotCard = forwardRef(function SnapshotCard(
  { monthLabel, netWorth, cashflow, streakCount, topInsight, currency, userName },
  ref,
) {
  const firstName = (userName || "").split(" ")[0] || "";
  const cashflowPos = cashflow >= 0;
  const netWorthColor = netWorth >= 0 ? "#E09E50" : "#C88B6E";

  return (
    <div
      ref={ref}
      style={{
        width: 1080,
        height: 1080,
        background:
          "radial-gradient(ellipse 900px 600px at 30% 20%, rgba(224,158,80,0.32) 0%, transparent 60%)," +
          "radial-gradient(ellipse 800px 600px at 80% 100%, rgba(67,122,85,0.32) 0%, transparent 55%)," +
          "linear-gradient(180deg, #1A2E22 0%, #12211A 100%)",
        color: "#FDFCFB",
        fontFamily: "var(--font-body)",
        padding: 72,
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        overflow: "hidden",
      }}
      data-testid="monthly-share-card"
    >
      {/* Header row */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <div style={{
            width: 56, height: 56, borderRadius: 12,
            background: "#2D5A40",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <Sprout size={30} color="#FDFCFB" strokeWidth={2.2} />
          </div>
          <div>
            <div style={{ fontSize: 14, letterSpacing: "0.32em", opacity: 0.7, textTransform: "uppercase" }}>Personal</div>
            <div style={{ fontFamily: "var(--font-heading)", fontSize: 22, fontWeight: 500, letterSpacing: "-0.01em" }}>
              Finance Optimizer
            </div>
          </div>
        </div>
        <div style={{
          fontFamily: "var(--font-mono)",
          fontSize: 16, letterSpacing: "0.08em",
          opacity: 0.6,
          textAlign: "right",
        }}>
          {monthLabel.toUpperCase()}
        </div>
      </div>

      {/* Big net-worth block */}
      <div>
        <div style={{
          fontSize: 20, letterSpacing: "0.36em", textTransform: "uppercase",
          color: "#E09E50", fontWeight: 500, marginBottom: 8,
        }}>
          {firstName ? `${firstName}'s snapshot` : "Snapshot"}
        </div>
        <div style={{
          fontFamily: "var(--font-heading)",
          fontSize: 72, fontWeight: 500, lineHeight: 1,
          letterSpacing: "-0.03em", marginBottom: 20,
        }}>
          Net worth
        </div>
        <div style={{
          fontFamily: "var(--font-mono)",
          fontSize: 136, fontWeight: 600, lineHeight: 1,
          letterSpacing: "-0.035em",
          color: netWorthColor,
        }}>
          {formatMoney(netWorth, currency)}
        </div>
      </div>

      {/* Stat trio */}
      <div style={{
        display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 24,
      }}>
        <SnapshotStat
          icon={cashflowPos
            ? <TrendingUp size={22} color="#437A55" />
            : <TrendingDown size={22} color="#C88B6E" />}
          label="Cashflow"
          value={`${cashflow >= 0 ? "+" : "−"}${formatMoney(Math.abs(cashflow), currency)}`}
          detail={cashflowPos ? "saved this month" : "over budget"}
          tint={cashflowPos ? "#437A55" : "#C88B6E"}
        />
        <SnapshotStat
          icon={<Flame size={22} color="#E09E50" />}
          label="Streak"
          value={`${streakCount}`}
          detail={streakCount === 1 ? "month" : "months"}
          tint="#E09E50"
        />
        <SnapshotStat
          icon={<Wallet size={22} color="#FDFCFB" />}
          label={topInsight ? "Top save" : "Track everything"}
          value={topInsight
            ? `${formatMoney(Math.round(topInsight.monthly_savings || 0), currency)}/mo`
            : "12 modules"}
          detail={topInsight ? "monthly opportunity" : "one clear picture"}
          tint="#FDFCFB"
        />
      </div>

      {/* Footer */}
      <div style={{
        display: "flex", alignItems: "flex-end", justifyContent: "space-between",
        paddingTop: 24, borderTop: "1px solid rgba(253,252,251,0.14)",
      }}>
        <div>
          <div style={{
            fontSize: 14, letterSpacing: "0.28em", textTransform: "uppercase",
            opacity: 0.55, marginBottom: 6,
          }}>
            Building the habit
          </div>
          <div style={{ fontSize: 22, opacity: 0.9 }}>One month at a time.</div>
        </div>
      </div>
    </div>
  );
});

function SnapshotStat({ icon, label, value, detail, tint }) {
  return (
    <div style={{
      background: "rgba(253,252,251,0.06)",
      border: "1px solid rgba(253,252,251,0.10)",
      borderRadius: 20,
      padding: 24,
    }}>
      <div style={{
        display: "flex", alignItems: "center", gap: 10, marginBottom: 12,
      }}>
        <div style={{
          width: 40, height: 40, borderRadius: 10,
          background: "rgba(253,252,251,0.08)",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          {icon}
        </div>
        <div style={{
          fontSize: 12, letterSpacing: "0.28em",
          textTransform: "uppercase", opacity: 0.6,
        }}>{label}</div>
      </div>
      <div style={{
        fontFamily: "var(--font-mono)",
        fontSize: 40, fontWeight: 600, lineHeight: 1,
        letterSpacing: "-0.025em",
        color: tint,
      }}>
        {value}
      </div>
      <div style={{ fontSize: 15, opacity: 0.65, marginTop: 8 }}>{detail}</div>
    </div>
  );
}

export default SnapshotCard;
